import { type MouseEvent, useCallback } from 'react';

import { components, type GroupBase, type OptionProps } from 'react-select';

import { Tooltip } from '@chakra-ui/react';

/**
 * Custom Option component for react-select.
 *
 * This component wraps the default `Option` from `react-select` and adds support for an
 * `allowClickableHeader` prop, which modifies the click behavior of the option.
 *
 * - If `allowClickableHeader` is `true`, the click event is handled and propagation is stopped.
 * - Otherwise, the click event is handled normally.
 *
 * Unused mouse event handlers (`onMouseMove`, `onMouseOver`) are removed from `innerProps`.
 *
 * @template Option - The option type.
 * @template IsMulti - Whether multiple values are allowed.
 * @template Group - The group type for options.
 *
 * @param props - The props for the option component, including `children` and react-select option props.
 * @returns The rendered custom option component.
 */
function Option<Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
  ...props
}: OptionProps<Option, IsMulti, Group>) {
  const {
    selectProps: { allowClickableHeader = false },
  } = props;
  const { onClick, onMouseMove, onMouseOver, ...innerProps } = props.innerProps;

  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (allowClickableHeader) {
        onClick?.(e);
        e.stopPropagation();

        return;
      }

      onClick?.(e);
    },
    [allowClickableHeader, onClick]
  );

  return (
    <components.Option
      className="hsp-select-option"
      {...{
        ...props,
        innerProps: {
          ...innerProps,
          onClick: handleOnClick,
        },
      }}>
      <Tooltip
        hasArrow
        label={children}
        p={3}
        placement="auto"
        rounded="lg">
        {children}
      </Tooltip>
    </components.Option>
  );
}

export default Option;
